import React from "react";
import styled from "styled-components";
import { Box } from "../Box";
import { Icon } from "../Icon";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import { style } from "../style";
import { Resource } from "./types";

type Props = {
  resources: Resource[];
  area?: "datacenter" | "region" | "vpc";
  disabled?: boolean;
};

const RESOURCE_ICON_MAP: Record<Resource["type"], string> = {
  droplet: "product-droplet",
  loadbalancer: "product-load-balancer",
  dbaas: "product-dbaas-primary",
  kubernetes: "product-k8s-cluster",
  spaces: "product-space",
};

const RESOURCE_NAME_MAP: Record<Resource["type"], string> = {
  droplet: "Droplet",
  loadbalancer: "Load Balancer",
  dbaas: "Database",
  kubernetes: "Kubernetes Cluster",
  spaces: "Spaces Bucket",
};

const ContentWrapper = styled.div`
  all: unset;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: ${style.vars.whitespace.small};
`;

const Count = styled.span`
  all: unset;
  font-size: ${style.vars.fontSize.small};
  font-weight: ${style.vars.fontWeight.base};
`;

const ResourceRowWrapper = styled.div`
  all: unset;

  display: flex;
  align-items: center;
  margin-bottom: ${style.vars.whitespace.small};
`;

const pluralize = (resourceName: string, count: number) => (count === 1 ? `1 ${resourceName}` : `${count} ${resourceName}s`);

export const ResourceCounter: React.FC<Props> = (props) => {
  const { resources, area = "datacenter", disabled = false } = props;

  const count = resources.reduce((acc, cur) => acc + cur.count, 0);

  const toolTipContent = React.useMemo(() => {
    return resources.map((resource) => {
      const { type, count } = resource;

      return (
        <ResourceRowWrapper key={type} data-testid={`${type}-resource-count`}>
          <Box mr={style.vars.whitespace.small}>
            <Icon icon={RESOURCE_ICON_MAP[type]} large />
          </Box>
          <span>{pluralize(RESOURCE_NAME_MAP[type], count)}</span>
        </ResourceRowWrapper>
      );
    });
  }, [resources]);

  let tooltipType: string;

  switch (area) {
    case "datacenter":
      tooltipType = "datacenter";
      break;
    case "region":
      tooltipType = "region";
      break;
    case "vpc":
      tooltipType = "VPC";
      break;
  }

  const tooltipTitle = (
    <Text>
      <strong>Active resources in this {tooltipType}</strong>
    </Text>
  );

  return (
    <Tooltip.Inline
      content={
        <ContentWrapper>
          {tooltipTitle}
          {toolTipContent}
        </ContentWrapper>
      }
      theme="light"
      disabled={disabled}
    >
      <Count>{pluralize("resource", count)}</Count>
    </Tooltip.Inline>
  );
};

ResourceCounter.displayName = "ResourceCounter";
